'use client';
import {DetailGroup} from 'common/components/detail-group';
import {DetailRow} from 'common/components/detail-row';
import {ListDetailRow, ListItem} from 'common/components/list-detail-row';
import {FC, Fragment} from 'react';
import {ExpansionPanel} from 'common/components/expansion-panel';
import {StringUtils} from 'common/utils/string-utils';
import {useTranslation} from 'react-i18next';
import {RolleDetailGroup} from 'features/rettsstiftelse/components/losore/rolle-detail-group';
import {FormuesgodeDetailRow} from 'features/rettsstiftelse/components/losore/formuesgode-detail-row';
import {createUseStyles} from 'react-jss';
import {Rettsstiftelse} from 'features/rettsstiftelse/model/losore/rettsstiftelse';
import {FdkText} from 'features/terms/components/fdk-text';
import {ConceptID, ConceptIDKey} from 'features/terms/felles-datakatalog/concept-ids';
import {GjeldsOrdningDetailRow} from 'features/rettsstiftelse/components/losore/gjeldsordning-detail-row';
import {useLang} from 'features/i18n/use-lang';
import {KLOKKESLETTPRIORITERING_INTRODUCTION_DATE} from 'features/rettsstiftelse/constants/dates';

const useRettsstiftelsePanelStyles = createUseStyles({
  vergemaalGjelder: {
    margin: '0',
  },
  vergemaalMerInfo: {
    margin: '1rem 0 0 0',
  },
});

export const rettsstiftelsesSort = (a: Rettsstiftelse, b: Rettsstiftelse) => {
  return +new Date(a.innkomsttidspunkt) - +new Date(b.innkomsttidspunkt);
};

export const RettsstiftelsePanel: FC<{rettsstiftelse: Rettsstiftelse; index: number}> = ({rettsstiftelse, index}) => {
  const {t} = useTranslation(['rettsstiftelser']);
  const appLanguage = useLang();

  const styles = useRettsstiftelsePanelStyles();

  const innkomsttidspunktDate = new Date(rettsstiftelse.innkomsttidspunkt);

  return (
    <ExpansionPanel
      key={rettsstiftelse.dokumentnummer}
      index={index}
      id={rettsstiftelse.dokumentnummer}
      title={rettsstiftelse.typeBeskrivelse}
      subtitle={`${t('rettsstiftelser:docNumber').toLowerCase()} ${rettsstiftelse.dokumentnummer}`}
      titleDescription={
        Object.keys(ConceptID).includes(rettsstiftelse.type.split('.')[1].toUpperCase()) && (
          <p>
            <b>{StringUtils.capitalize(rettsstiftelse.typeBeskrivelse)}</b>
            <FdkText conceptId={ConceptID[rettsstiftelse.type.split('.')[1].toUpperCase() as ConceptIDKey]} />
          </p>
        )
      }
      defaultContent={
        <DetailGroup id={`defaultContent_${rettsstiftelse.dokumentnummer}`} isDefaultContent={true}>
          <DetailRow
            label={t('rettsstiftelser:innkomsttidspunkt')}
            isDefaultContentRow={true}
            description={<FdkText conceptId={ConceptID.INNKOMSTTIDSPUNKT} />}
          >
            {innkomsttidspunktDate >= KLOKKESLETTPRIORITERING_INTRODUCTION_DATE
              ? StringUtils.formatDateTime(innkomsttidspunktDate, appLanguage, t)
              : StringUtils.formatDate(innkomsttidspunktDate, appLanguage)}
          </DetailRow>
          <DetailRow label={t('rettsstiftelser:statusBeskrivelseLabel')} isDefaultContentRow={true}>
            {t(
              `rettsstiftelser:statusBeskrivelse.${rettsstiftelse.status}`,
              StringUtils.capitalize(rettsstiftelse.statusBeskrivelse)
            )}
          </DetailRow>
        </DetailGroup>
      }
    >
      {rettsstiftelse.roller && (
        <RolleDetailGroup id={`roller_${rettsstiftelse.dokumentnummer}`} rettsstiftelse={rettsstiftelse} />
      )}

      <DetailGroup id={`formuesgoder_${rettsstiftelse.dokumentnummer}`}>
        {!!rettsstiftelse.formuesgoder?.length && (
          <FormuesgodeDetailRow formuesgoder={rettsstiftelse.formuesgoder} rsType={rettsstiftelse.type} />
        )}

        {(rettsstiftelse.krav?.kravSalgspant || rettsstiftelse.krav?.kravFordringer) && (
          <DetailRow label={t('rettsstiftelser:krav')}>
            {rettsstiftelse.krav.kravSalgspantBeskrivelse &&
              StringUtils.capitalize(rettsstiftelse.krav.kravSalgspantBeskrivelse)}
            {rettsstiftelse.krav.kravFordringerBeskrivelse &&
              StringUtils.capitalize(rettsstiftelse.krav.kravFordringerBeskrivelse)}
          </DetailRow>
        )}

        {rettsstiftelse.krav?.belop && (
          <ListDetailRow label={t('rettsstiftelser:belop')}>
            {rettsstiftelse.krav?.belop.map(k => (
              <ListItem key={`${k.valuta}${k.belop}`}>
                <span className='sr-only'>{`${k.valuta}${k.belop}`}</span>
                <span aria-hidden={true}>{StringUtils.formatBelop(k.belop, k.valuta, appLanguage)}</span>
              </ListItem>
            ))}
          </ListDetailRow>
        )}

        {rettsstiftelse.skifteutlegg?.typeBeskrivelse && (
          <DetailRow label={t('rettsstiftelser:skifteutlegg')}>{rettsstiftelse.skifteutlegg.typeBeskrivelse}</DetailRow>
        )}

        {rettsstiftelse?.gjeldsordning && <GjeldsOrdningDetailRow gjeldsordning={rettsstiftelse.gjeldsordning} />}

        {rettsstiftelse?.vergemaal && (
          <>
            <DetailRow label={t('rettsstiftelser:vergemal.omfatter')}>
              {rettsstiftelse.vergemaal.gjelderPersonligeForhold && (
                <div className={styles.vergemaalGjelder}>{t('rettsstiftelser:vergemal.personlig')}</div>
              )}
              {rettsstiftelse.vergemaal.gjelderOkonomiskeForhold && (
                <div className={styles.vergemaalGjelder}>{t('rettsstiftelser:vergemal.okonomisk')}</div>
              )}
              <div className={styles.vergemaalMerInfo}>
                {t('rettsstiftelser:vergemal.merInfo', {context: rettsstiftelse.type})}
              </div>
            </DetailRow>
            {rettsstiftelse.vergemaal.varighetBeskrivelse && (
              <DetailRow label={t('rettsstiftelser:vergemal.er')}>
                {rettsstiftelse.vergemaal.varighetBeskrivelse}
              </DetailRow>
            )}
            {rettsstiftelse.vergemaal.tidsbegrensetTilDato && (
              <DetailRow label={t('rettsstiftelser:vergemal.tidsbegrensetTilDato')}>
                {StringUtils.formatDate(new Date(rettsstiftelse.vergemaal.tidsbegrensetTilDato), appLanguage)}
              </DetailRow>
            )}
          </>
        )}

        {rettsstiftelse?.tidsbegrensningHeftelse && (
          <DetailRow label={t('rettsstiftelser:vergemal.tidsbegrensetTilDato')}>
            {StringUtils.formatDate(new Date(rettsstiftelse.tidsbegrensningHeftelse), appLanguage)}
          </DetailRow>
        )}

        {!!rettsstiftelse?.gjeldsomfang && (
          <DetailRow label={t('rettsstiftelser:gjeldsomfangLabel')}>{rettsstiftelse.gjeldsomfangBeskrivelse}</DetailRow>
        )}

        {rettsstiftelse?.avgrensingRettsstiftelse && (
          <DetailRow label={t('rettsstiftelser:avgrensingRettsstiftelse')}>
            {StringUtils.capitalize(rettsstiftelse.avgrensingRettsstiftelse)}
          </DetailRow>
        )}

        {!!rettsstiftelse.paategninger?.length && (
          <ListDetailRow
            label={t('rettsstiftelser:paategning')}
            description={<FdkText conceptId={ConceptID.PAATEGNING} />}
          >
            {rettsstiftelse.paategninger?.map((paategning, idx) => (
              <ListItem key={idx}>{paategning}</ListItem>
            ))}
          </ListDetailRow>
        )}

        {rettsstiftelse.prioritetsvikelser?.map((pv, idx) => (
          <DetailRow
            label={t('rettsstiftelser:prioritetsvikelse')}
            description={<FdkText conceptId={ConceptID.PRIORITETSVIKELSE} />}
            key={idx}
          >
            {pv.dokumentnummer && (
              <>
                {t('rettsstiftelser:docNumber')}: {pv.dokumentnummer}
              </>
            )}

            {pv.rettighetshaverFremtidig && (
              <Fragment>
                {t('rettsstiftelser:rettighetshaverFremtidig')}: {pv.rettighetshaverFremtidig}
              </Fragment>
            )}
            {pv.belopFremtidig && (
              <div>
                {t('rettsstiftelser:belop')}:
                <span className='sr-only'>{`${pv.belopFremtidig.valuta}${pv.belopFremtidig.belop}`}</span>
                <span aria-hidden={true}>
                  {StringUtils.formatBelop(pv.belopFremtidig.belop, pv.belopFremtidig.valuta, appLanguage)}
                </span>
              </div>
            )}
          </DetailRow>
        ))}
        {rettsstiftelse.saerskiltPrioritet && (
          <DetailRow label={t('rettsstiftelser:saerskiltPrioritet')}>
            {rettsstiftelse.saerskiltPrioritet.typeBeskrivelse}
          </DetailRow>
        )}
        {rettsstiftelse.konkurs?.erTvangsavviklingEllerTvangsopplosning && (
          <p>{t('rettsstiftelser:konkurs.erTvangsavviklingEllerTvangsopplosning')}</p>
        )}
      </DetailGroup>
    </ExpansionPanel>
  );
};

export default RettsstiftelsePanel;
